import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from '../components/States';
import { Dropdown, MenuItem } from '../components/FilterBar';
import { useWeapons } from '../hooks/useWeapons';
import { useI18n } from '../i18n';
import { useCurrency } from '../hooks/useCurrency';
import { cleanName } from '../lib/format';
import { KitBuilder } from '../components/KitBuilder';
import type { CorDaPaleta, Kit, SkinGroup } from '../api/types';
import { BotaoKitNaSacola } from '../components/BotaoKitNaSacola';
import { itemDoGrupo, reais, trackSelectItem, trackViewItemList } from '../lib/tracking';

/**
 * A vitrine dos kits prontos.
 *
 * Um kit é uma faca e uma luva que combinam, com desconto que só existe
 * levando as duas. A página é sugestão, não catálogo: são poucos, escolhidos
 * a dedo no painel, e o que convence é ver as duas peças juntas com a conta
 * pronta ao lado.
 */
const LISTA_KITS = { id: 'kits', nome: 'Kits prontos' };

export function KitsPage() {
  const { money, rate } = useCurrency();
  const { t } = useI18n();
  // As faixas de preço são as mesmas do catálogo: o cliente que acabou de
  // filtrar as skins encontra aqui as opções que já conhece.
  const { data: taxonomia } = useWeapons();
  const [items, setItems] = useState<Kit[] | null>(null);
  const [cores, setCores] = useState<CorDaPaleta[]>([]);
  // As cores ficam no endereço: um link de "kits azuis" abre já filtrado.
  const [params, setParams] = useSearchParams();
  const filtro = (params.get('cor') ?? '').split(',').filter(Boolean);
  // O preço também fica no endereço, pelo mesmo motivo das cores: o link de
  // "kits até R$ 3.000" abre já filtrado.
  const faixaUrl = { min: params.get('pmin') ?? '', max: params.get('pmax') ?? '' };
  // Os campos livres são digitados antes de valer: só o "Aplicar" (ou Enter)
  // muda o endereço, senão a lista piscaria a cada tecla.
  const [precoMin, setPrecoMin] = useState(faixaUrl.min);
  const [precoMax, setPrecoMax] = useState(faixaUrl.max);
  useEffect(() => {
    setPrecoMin(faixaUrl.min);
    setPrecoMax(faixaUrl.max);
  }, [faixaUrl.min, faixaUrl.max]);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    void api
      .kits()
      .then((r) => {
        setItems(r.items);
        setCores(r.cores ?? []);
      })
      .catch((e) => setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os kits.'));
  }, []);

  // A vitrine de kits como lista: cada kit pronto é um item, pelo preço de kit.
  useEffect(() => {
    if (!items || items.length === 0) return;
    trackViewItemList(
      LISTA_KITS,
      items.map((k, i) => ({
        item_id: `kit:${k.id}`,
        item_name: k.name,
        price: reais(k.precoCny),
        index: i,
      })),
    );
  }, [items]);

  if (erro) return <Banner kind="error">{erro}</Banner>;
  if (!items) return <Spinner label="Montando os kits…" />;
  // "até X%" sai do maior desconto que existe de pé. Fixar um número na mão
  // viraria promessa que nenhum kit cumpre no dia em que os descontos mudarem.
  // Sem kit pronto não há número honesto para prometer, e o título fica sem ele.
  //
  // A página não cai no estado vazio quando não há kit pronto: o montador vale
  // sozinho, e esconder ele justo quando o painel está vazio tirava do cliente
  // o único caminho para comprar um kit.
  const maiorDesconto = items.length > 0 ? Math.max(...items.map((k) => k.descontoPct)) : 0;

  // Um kit entra se tiver qualquer uma das cores marcadas: quem marca azul e
  // preto quer ver os dois, não só o raro kit que tem as duas.
  // O kit guarda o preço em yuan, como o resto do catálogo; o filtro é digitado
  // em reais, que é o que o cliente vê. Sem câmbio não dá para comparar, e aí o
  // filtro de preço fica de fora em vez de esconder kits por engano.
  const emReais = (precoCny: number) => (rate ? precoCny * rate.rate : null);
  const numero = (v: string) => {
    const n = Number(v.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) && v.trim() !== '' ? n : null;
  };
  const min = numero(faixaUrl.min);
  const max = numero(faixaUrl.max);
  const ordenar = (lista: Kit[]) => {
    const emReaisOuZero = (k: Kit) => emReais(k.precoCny) ?? 0;
    if (ordem === 'preco_asc') return [...lista].sort((a, b) => emReaisOuZero(a) - emReaisOuZero(b));
    if (ordem === 'preco_desc') return [...lista].sort((a, b) => emReaisOuZero(b) - emReaisOuZero(a));
    if (ordem === 'desconto') return [...lista].sort((a, b) => b.descontoPct - a.descontoPct);
    // Sem escolha, vale a ordem do painel: é a vitrine que a loja montou.
    return lista;
  };
  const visiveis = items.filter((k) => {
    if (filtro.length > 0 && !k.colors.some((c) => filtro.includes(c))) return false;
    const brl = emReais(k.precoCny);
    if (brl === null) return true;
    if (min !== null && brl < min) return false;
    if (max !== null && brl > max) return false;
    return true;
  });
  const ORDENS: { valor: string; rotulo: string }[] = [
    { valor: '', rotulo: 'Sugestão da loja' },
    { valor: 'preco_asc', rotulo: 'Preço ↑' },
    { valor: 'preco_desc', rotulo: 'Preço ↓' },
    { valor: 'desconto', rotulo: 'Maior desconto' },
  ];

  const trocarFiltro = (proximas: string[]) => {
    const novo = new URLSearchParams(params);
    if (proximas.length > 0) novo.set('cor', proximas.join(','));
    else novo.delete('cor');
    setParams(novo, { replace: true });
  };
  const trocarPreco = (min: number | null, max: number | null) => {
    const novo = new URLSearchParams(params);
    if (min !== null) novo.set('pmin', String(min));
    else novo.delete('pmin');
    if (max !== null) novo.set('pmax', String(max));
    else novo.delete('pmax');
    setParams(novo, { replace: true });
  };
  const aplicarFaixa = () => {
    const a = numero(precoMin);
    const b = numero(precoMax);
    // Invertidos, corrige em vez de devolver lista vazia.
    trocarPreco(a !== null && b !== null && a > b ? b : a, a !== null && b !== null && a > b ? a : b);
  };
  const brlCurto = (v: number) => `R$ ${v}`;
  const ordem = params.get('ordem') ?? '';
  const rotuloPreco = (() => {
    if (min === null && max === null) return t('filters.priceAny');
    if (max === null) return t('filters.priceAbove', { min: brlCurto(min!) });
    if (min === null) return `≤ ${brlCurto(max)}`;
    return `${brlCurto(min)} – ${brlCurto(max)}`;
  })();
  const limparTudo = () => setParams(new URLSearchParams(), { replace: true });
  const temFiltro = filtro.length > 0 || precoMin !== '' || precoMax !== '';
  const nomeDaCor = new Map(cores.map((c) => [c.key, c]));
  const trocarOrdem = (valor: string) => {
    const novo = new URLSearchParams(params);
    if (valor) novo.set('ordem', valor);
    else novo.delete('ordem');
    setParams(novo, { replace: true });
  };
  const rotuloCor =
    filtro.length === 0
      ? t('filters.all')
      : filtro.length === 1
        ? (nomeDaCor.get(filtro[0]!)?.pt ?? filtro[0]!)
        : `${nomeDaCor.get(filtro[0]!)?.pt ?? filtro[0]!} +${filtro.length - 1}`;

  return (
    <div className="kits-pagina">
      <header className="kits-topo">
        <h1>Kits prontos</h1>
        <p className="muted">
          Faca e luva escolhidas para combinar. Levando as duas juntas, elas saem por uma cotação melhor do que
          compradas separadas — o preço riscado é o que cada uma custa sozinha.
        </p>
      </header>

      {/* Os mesmos filtros do catálogo: Preço e Cor, na mesma pílula. Antes a
          aba tinha um visual próprio, e cada tela ensinava um jeito diferente
          de filtrar. */}
      <div className="kits-filtros">
        <Dropdown
          label="Ordenar"
          value={ORDENS.find((o) => o.valor === ordem)?.rotulo ?? ORDENS[0]!.rotulo}
          active={ordem !== ''}
        >
          {ORDENS.map((o) => (
            <MenuItem key={o.valor || 'padrao'} active={ordem === o.valor} onClick={() => trocarOrdem(o.valor)}>
              {o.rotulo}
            </MenuItem>
          ))}
        </Dropdown>

        <Dropdown label={t('filters.price')} value={rotuloPreco} active={min !== null || max !== null}>
          <MenuItem active={min === null && max === null} onClick={() => trocarPreco(null, null)}>
            {t('filters.priceAny')}
          </MenuItem>
          {(taxonomia?.priceRanges ?? []).map((f) => (
            <MenuItem
              key={f.key}
              active={min === f.min && max === f.max}
              onClick={() => trocarPreco(f.min, f.max)}
            >
              {f.max === null ? t('filters.priceAbove', { min: brlCurto(f.min) }) : `${brlCurto(f.min)} – ${brlCurto(f.max)}`}
            </MenuItem>
          ))}

          {/* A faixa livre existe porque kit é item caro: as faixas prontas
              param em R$ 1000, e quem procura um par de R$ 4 mil precisa
              digitar. Mesmo bloco do catálogo. */}
          <div className="fdrop-range">
            <input
              className="input"
              inputMode="decimal"
              placeholder={t('filters.priceMin')}
              value={precoMin}
              onChange={(e) => setPrecoMin(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && aplicarFaixa()}
              aria-label={t('filters.priceMin')}
            />
            <span>–</span>
            <input
              className="input"
              inputMode="decimal"
              placeholder={t('filters.priceMax')}
              value={precoMax}
              onChange={(e) => setPrecoMax(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && aplicarFaixa()}
              aria-label={t('filters.priceMax')}
            />
            <button className="btn small" onClick={aplicarFaixa}>
              {t('filters.priceApply')}
            </button>
          </div>
        </Dropdown>

        {cores.length > 0 && (
          <Dropdown label={t('filters.color')} value={rotuloCor} active={filtro.length > 0}>
            <MenuItem active={filtro.length === 0} onClick={() => trocarFiltro([])}>
              {t('filters.all')}
            </MenuItem>
            {cores.map((c) => (
              <MenuItem
                key={c.key}
                active={filtro.includes(c.key)}
                color={c.hex}
                onClick={() => trocarFiltro(filtro.includes(c.key) ? filtro.filter((k) => k !== c.key) : [...filtro, c.key])}
              >
                {c.pt}
              </MenuItem>
            ))}
          </Dropdown>
        )}

        {temFiltro && (
          <button type="button" className="fclear" onClick={limparTudo}>
            {t('filters.clear')}
          </button>
        )}
      </div>

      <div className="kits-grid">
        {/*
          O convite vem primeiro: explica a ideia antes dos exemplos, e alcança
          quem não se interessou por nenhum dos pares já montados — que é
          justamente quem a loja quer trazer para o kit.
        */}
        <KitBuilder maiorDesconto={maiorDesconto} />
        {ordenar(visiveis).map((k) => (
          <CartaoKit key={k.id} kit={k} money={money} cores={k.colors.flatMap((c) => nomeDaCor.get(c) ?? [])} />
        ))}
        {visiveis.length === 0 && items.length > 0 && (
          <p className="muted kits-sem-cor">
            Nenhum kit pronto com esses filtros. Monte o seu no quadro acima — o desconto de kit vale para qualquer par.
          </p>
        )}
      </div>
    </div>
  );
}

function CartaoKit({
  kit,
  money,
  cores,
}: {
  kit: Kit;
  money: (v: number | null | undefined) => string;
  cores: CorDaPaleta[];
}) {
  const pct = Math.round(kit.descontoPct);

  return (
    <article className="kit-card">
      <header className="kit-card-topo">
        <h2>{kit.name}</h2>
        {pct > 0 && <span className="kit-selo">−{pct}%</span>}
      </header>
      {cores.length > 0 && (
        <div className="kit-card-cores">
          {cores.map((c) => (
            <span key={c.key} className="kit-card-cor">
              <span className="kit-cor-bola" style={{ background: c.hex }} />
              {c.pt}
            </span>
          ))}
        </div>
      )}
      {kit.note && <p className="kit-recado">{kit.note}</p>}

      <div className="kit-card-pecas">
        <PecaDoKit grupo={kit.knife} desgaste={kit.knifeExteriorLabel} money={money} />
        <span className="kit-mais">+</span>
        <PecaDoKit grupo={kit.gloves} desgaste={kit.glovesExteriorLabel} money={money} />
      </div>

      <div className="kit-card-conta">
        {/* A soma riscada é o argumento inteiro: sem ela o preço do kit é só
            um número, e o cliente não tem como ver que ganhou alguma coisa. */}
        <span className="kit-soma">{money(kit.somaCny)}</span>
        <b className="kit-preco">{money(kit.precoCny)}</b>
        {kit.economiaCny > 0 && <span className="kit-economia">economize {money(kit.economiaCny)}</span>}
      </div>

      <BotaoKitNaSacola
        knife={{ grupo: kit.knife, exterior: kit.knifeExterior }}
        gloves={{ grupo: kit.gloves, exterior: kit.glovesExterior }}
      />
    </article>
  );
}

/** Uma das duas peças, clicável: o cliente quer ver a skin antes de decidir. */
function PecaDoKit({
  grupo,
  desgaste,
  money,
}: {
  grupo: SkinGroup;
  desgaste: string | null;
  money: (v: number | null | undefined) => string;
}) {
  // Com o desgaste fixado o preço é daquela variante, e o "a partir de" sairia
  // mentindo: o kit entrega exatamente esta peça, não a mais barata.
  const preco = desgaste
    ? (grupo.variants.find((v) => v.exterior?.pt === desgaste || v.exterior?.code === desgaste)?.price ?? null)
    : grupo.minPrice;

  return (
    <Link
      className="kit-peca"
      to={`/all/s/${encodeURIComponent(grupo.key)}`}
      onClick={() => trackSelectItem(LISTA_KITS, itemDoGrupo(grupo))}
    >
      {grupo.iconUrl ? <img src={grupo.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
      <b>{cleanName(grupo.name)}</b>
      {desgaste && <span className="kit-peca-desgaste">{desgaste}</span>}
      <span className="dim">
        {desgaste ? '' : 'a partir de '}
        {money(preco)}
      </span>
    </Link>
  );
}
